import classNames from 'classnames/bind';
import isEqual from 'lodash/isEqual';
import Link from 'next/link';
import { Fragment, useState } from 'react';

import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import { PriceAlertFormContainerRangeIcon } from 'asset/svg';
import { DateFormat } from 'common/types';
import { days } from 'common/utils';
import { IModalComponent, Modal, ModalPosition } from 'contexts/modalFunc';
import {
  getPassengerDesc,
  getSeatDesc,
  tripTypes,
} from 'contexts/searchParams';
import {
  PriceChangesActive,
  defaultPriceChangesActive,
  usePriceAlertNightConsent,
  usePriceChangesSubscriptions,
} from 'hooks/usePriceChangesSubscriptions';
import { PriceChangesSubscription, TripType } from 'types/apollo';
import styles from '../index.module.scss';
import SubscriptionLimitAlert from '../subscription-limit-alert';
import AdConsentToggle from './ad-consent-toggle';
import CreateButton from './create-button';
import DeleteButton from './delete-button';
import NightAlertConsent from './night-alert-consent';
import SubscriptionStatus from './subscription-status';
import UpdateButton from './update-button';

const cx = classNames.bind(styles);

const EXPIRATION_TIME_IN_DAYS = 7;
const MAX_SUBSCRIPTION_COUNT = 10;

export default function PriceAlertFormContainer({
  modalInfo: { payload },
  setModal,
}: IModalComponent) {
  const { isNew, subscription } = payload!;
  const {
    searchKey,
    adultCount,
    childCount,
    infantCount,
    cabinClass,
    isNonstop,
    itineraries,
    itineraries: [
      {
        departureLocationCode,
        departureLocationName,
        arrivalLocationCode,
        arrivalLocationName,
      },
    ],
    tripType,
    createdAt,
    originalMinPrice,
  }: PriceChangesSubscription = subscription;
  const updatedMinPrice =
    payload!.updatedMinPrice || subscription.updatedMinPrice;
  const isMinPriceUpdated =
    updatedMinPrice && originalMinPrice !== updatedMinPrice;
  const latestMinPrice = updatedMinPrice || originalMinPrice;
  const initialPriceChangesActive = isMinPriceUpdated
    ? defaultPriceChangesActive
    : subscription.changes;

  const ref = useOffClick(onClose, { stopPropagation: true });
  const { loading, error, data } = usePriceAlertNightConsent();
  const [changes, setChanges] = useState<PriceChangesActive>(
    initialPriceChangesActive,
  );
  const [showNightAlertConsentPopup, setShowNightAlertConsentPopup] =
    useState(false);
  const { subscriptions } = usePriceChangesSubscriptions();

  const selected = Object.entries(changes)
    .filter(([, active]) => active)
    .map(([ratio]) => +ratio);
  const isDisabled =
    isEqual(changes, initialPriceChangesActive) || !selected.length;

  function onClose() {
    setModal({ type: Modal.NULL, position: ModalPosition.MAIN });
  }

  function onCompleted() {
    if (
      !loading &&
      !error &&
      (!data?.priceAlertNightConsent ||
        (!data.priceAlertNightConsent.isConsented &&
          days().diff(data.priceAlertNightConsent.updatedAt, 'day') >
            EXPIRATION_TIME_IN_DAYS))
    ) {
      setShowNightAlertConsentPopup(true);
    } else {
      onClose();
    }
  }

  if (isNew && subscriptions.length === MAX_SUBSCRIPTION_COUNT) {
    return <SubscriptionLimitAlert tripType={tripType} onClose={onClose} />;
  }

  if (showNightAlertConsentPopup) {
    return <NightAlertConsent onClose={onClose} />;
  }

  return (
    <div className={cx('PriceAlertFormContainer')} aria-modal="true" ref={ref}>
      <div className={cx('modal')}>
        <div className={cx('header')}>
          <h4 className={cx('title')}>가격 변동 알림 받기</h4>
          <button
            type="button"
            className={cx('close')}
            onClick={onClose}
            data-event-nlog="click"
            data-event-area-code={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.close`}
            aria-label="닫기"
          />
        </div>
        <div className={cx('content')}>
          <div className={cx('current')}>
            <div className={cx('top')}>
              <strong className={cx('route')}>
                <span className={cx('airport')}>
                  <i className={cx('name')}>{departureLocationName}</i>
                  <b className={cx('code')}>{departureLocationCode}</b>
                </span>
                <span className={cx('airport')}>
                  <i className={cx('name')}>{arrivalLocationName}</i>
                  <b className={cx('code')}>{arrivalLocationCode}</b>
                </span>
              </strong>
              <SubscriptionStatus
                isSubscribed={!isNew}
                searchKey={searchKey}
                onCompleted={onClose}
              />
            </div>
            <div className={cx('current_desc')}>
              {itineraries.map(({ departureDate }, index) => (
                <b key={index} className={cx('date')}>
                  {days(departureDate, DateFormat.YMD).format(
                    DateFormat.DotMDd,
                  )}
                </b>
              ))}
              <em className={cx('meta')}>
                {tripTypes.get(tripType as any)}
                {isNonstop && ', 직항'}
              </em>
              <b className={cx('meta')}>
                {getPassengerDesc({
                  adult: adultCount,
                  child: childCount,
                  infant: infantCount,
                })}
                , {getSeatDesc(false, cabinClass)}
              </b>
            </div>
          </div>
          {isNew && (
            <div className={cx('info')}>
              원하는 가격에 도달하면 하루 1번
              <br />
              톡톡 알림을 보내드려요 (노선별 1일 1회)
            </div>
          )}
          {isMinPriceUpdated && (
            <div className={cx('info')}>
              마지막으로 알림을 설정한 날과
              <br />
              최저가가 달라졌어요!
            </div>
          )}
          <section className={cx('setting')}>
            <div className={cx('header')}>
              <h5 className={cx('title')}>알림 가격대 설정</h5>
              <em className={cx('sub')}>성인 1인 모든 결제수단 기준</em>
            </div>
            <div className={cx('list')}>
              <div className={cx('item')}>
                <div className={cx('price')}>
                  <div role="row">
                    <div className={cx('latest')}>
                      <b className={cx('value')}>
                        {withPriceSeperator(latestMinPrice)}
                      </b>
                      <span className={cx('unit')}>원</span>
                    </div>
                    <span className={cx('desc')}>현재 최저가</span>
                  </div>
                  {isMinPriceUpdated && (
                    <div role="row">
                      <div className={cx('original')}>
                        <s className={cx('value')}>
                          {withPriceSeperator(originalMinPrice)}
                        </s>
                        <span className={cx('unit')}>원</span>
                      </div>
                      <span className={cx('created')}>
                        {days(createdAt).format('YY.MM.DD.')} 최저가
                      </span>
                    </div>
                  )}
                  <div className={cx('price_info')}>
                    같은 도시 주변 공항의 항공편이 포함된 최저가예요
                  </div>
                </div>
              </div>
              {Object.entries(changes)
                .map(([ratio]) => +ratio)
                .sort((a, b) => b - a)
                .map((ratio, index) => (
                  <div key={ratio} className={cx('item')}>
                    <div className={cx('price')}>
                      <div role="row">
                        <PriceAlertFormContainerRangeIcon
                          className={cx('icon')}
                        />
                        <div className={cx('latest')}>
                          <b className={cx('value')}>
                            {withPriceSeperator(
                              Math.floor((latestMinPrice * ratio) / 100),
                            )}
                          </b>
                          <span className={cx('unit')}>원</span>
                        </div>
                        <span className={cx('ratio')}>{100 - ratio}%</span>
                      </div>
                      {isMinPriceUpdated && subscription.changes[ratio] && (
                        <div role="row">
                          <div className={cx('original')}>
                            <s className={cx('value')}>
                              {withPriceSeperator(
                                Math.floor((originalMinPrice * ratio) / 100),
                              )}
                            </s>
                            <span className={cx('unit')}>원</span>
                          </div>
                          <span className={cx('created')}>
                            {days(createdAt).format('YY.MM.DD.')} 등록가
                          </span>
                        </div>
                      )}
                    </div>
                    <div className={cx('btn')}>
                      <button
                        type="button"
                        className={cx('add')}
                        aria-checked={!!changes[ratio]}
                        data-event-nlog="click"
                        data-event-area-code={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.price${isNew ? '' : 'chg'}${index + 1}`}
                        onClick={() =>
                          setChanges(prev => ({
                            ...prev,
                            [ratio]: !prev[ratio],
                          }))
                        }
                      >
                        {changes[ratio] ? '등록완료' : '등록하기'}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            {!isDisabled && (
              <p className={cx('note')}>
                {selected
                  .sort((a, b) => b - a)
                  .map((ratio, i) => (
                    <Fragment key={ratio}>
                      {i > 0 && ', '}
                      <b>
                        {withPriceSeperator(
                          Math.floor((latestMinPrice * ratio) / 100),
                        )}
                      </b>
                      원
                    </Fragment>
                  ))}
                에 도달하면
                {isMinPriceUpdated && ' 변경된 가격'} 알림을 받습니다.
              </p>
            )}
          </section>
          {!subscriptions.length && <AdConsentToggle />}
        </div>
        {isNew ? (
          <div className={cx('footer')}>
            <div className={cx('btns')}>
              <button
                type="button"
                className={cx('cancel')}
                onClick={onClose}
                data-event-nlog="click"
                data-event-area-code={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.cancel`}
              >
                취소
              </button>
              <CreateButton
                changes={selected}
                clickCode={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.ok`}
                isDisabled={isDisabled}
                subscription={subscription}
                onCompleted={onCompleted}
              >
                적용 {selected.length !== 0 && selected.length}
              </CreateButton>
            </div>
          </div>
        ) : isMinPriceUpdated ? (
          <div className={cx('footer')}>
            <div className={cx('btns')}>
              <button
                type="button"
                className={cx('cancel')}
                onClick={onClose}
                data-event-nlog="click"
                data-event-area-code={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.chgcancel`}
              >
                취소
              </button>
              <UpdateButton
                changes={selected}
                clickCode={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.chgok`}
                isDisabled={isDisabled}
                minPrice={latestMinPrice}
                searchKey={searchKey}
                onCompleted={onCompleted}
              >
                변경된 가격 적용 {selected.length !== 0 && selected.length}
              </UpdateButton>
            </div>
          </div>
        ) : (
          <div className={cx('footer')}>
            <div className={cx('btns')}>
              <Link
                className={cx('more')}
                href="/price-alerts"
                onClick={onClose}
                data-event-nlog="click"
                data-event-area-code={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.more`}
              >
                가격알림 목록
              </Link>
              {!selected.length ? (
                <DeleteButton searchKey={searchKey} onCompleted={onClose}>
                  알림 삭제
                </DeleteButton>
              ) : (
                <UpdateButton
                  changes={selected}
                  clickCode={`${tripType === TripType.Ow ? 'lon' : 'lrd'}.change`}
                  isDisabled={isDisabled}
                  minPrice={latestMinPrice}
                  searchKey={searchKey}
                  onCompleted={onCompleted}
                >
                  알림 변경 {selected.length !== 0 && selected.length}
                </UpdateButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
